import type { AuthChangeEvent, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";

export interface AuthService {
  getSession(): Promise<Session | null>;
  signInWithPassword(credentials: { email: string; password: string }): Promise<void>;
  signOut(): Promise<void>;
  setSession(tokens: { access_token: string; refresh_token: string }): Promise<void>;
  onAuthStateChange(callback: (event: AuthChangeEvent, session: Session | null) => void): { unsubscribe: () => void };
  invokeFunction<TResponse>(name: string, body: Record<string, unknown>): Promise<{ data: TResponse | null; error: unknown }>;
}

export const supabaseAuthService: AuthService = {
  async getSession() {
    const { data } = await supabase.auth.getSession();
    return data.session;
  },

  async signInWithPassword(credentials) {
    const { error } = await supabase.auth.signInWithPassword(credentials);
    if (error) throw error;
  },

  async signOut() {
    await supabase.auth.signOut();
  },

  async setSession(tokens) {
    const { error } = await supabase.auth.setSession(tokens);
    if (error) throw error;
  },

  onAuthStateChange(callback) {
    const { data } = supabase.auth.onAuthStateChange(callback);
    return { unsubscribe: () => data.subscription.unsubscribe() };
  },

  async invokeFunction<TResponse>(name, body) {
    const { data, error } = await supabase.functions.invoke(name, { body });
    return { data: (data as TResponse) ?? null, error };
  },
};
